const SCRIPT_SELECTOR = 'script[type="application/x-sd-deferred-script"][data-sd-widget]';
const CAPTCHA_SELECTOR = '#recaptcha, #recaptcha-2, .g-recaptcha';
const INSTALLATION = '__sdDeferredWidgets';
const MARGIN = 300;

function matches(element, selector) {
  const match = element.matches || element.msMatchesSelector || element.webkitMatchesSelector;
  return match && match.call(element, selector);
}

function isDisplayed(element, doc, win, cache) {
  if (!doc.documentElement.contains(element)) {
    return false;
  }
  const visited = [];
  let visible = true;
  for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
    if (cache.has(node)) {
      visible = cache.get(node);
      break;
    }
    visited.push(node);
    const style = win.getComputedStyle(node);
    if (node.hidden || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') {
      visible = false;
      break;
    }
  }
  visited.forEach(node => cache.set(node, visible));
  return visible;
}

// An explicit reCAPTCHA container is empty until the API renders it. Observe
// its form (or nearest laid-out wrapper) when the container has no box yet.
function captchaAnchor(target, doc, rectangle) {
  for (let node = target; node && node !== doc.body && node !== doc.documentElement; node = node.parentElement) {
    const rect = rectangle(node);
    if (rect.bottom > rect.top && rect.right > rect.left) {
      return node;
    }
  }
  return target;
}

function mapHost(script) {
  for (let node = script.parentElement; node; node = node.parentElement) {
    if (node.hasAttribute('data-sd-widget-host')) {
      return node;
    }
  }
  return null;
}

function nearViewport(element, doc, win, rectangle) {
  const rect = rectangle(element);
  const height = win.innerHeight || doc.documentElement.clientHeight;
  const width = win.innerWidth || doc.documentElement.clientWidth;
  return rect.bottom >= -MARGIN && rect.top <= height + MARGIN &&
    rect.right >= -MARGIN && rect.left <= width + MARGIN;
}

/** Install once per document. The caller starts this after DOMContentLoaded.
 * Returns a disposer for a page teardown; scripts already started keep loading.
 */
export function installDeferredWidgets(doc) {
  if (doc[INSTALLATION]) {
    return doc[INSTALLATION];
  }

  const win = doc.defaultView;
  let pending = [];
  let captchas = [];
  let observed = [];
  let stopped = false;
  let recaptchaStarted = false;
  let intersectionObserver;
  let mutationObserver;
  // Keep intersection state and the selected CAPTCHA anchor, never DOMRects
  // or computed styles. Moves and resize require one fresh geometry check.
  const intersections = new Map();
  const captchaAnchors = new Map();

  function invalidateAnchor(anchor) {
    const state = intersections.get(anchor);
    if (state) {
      state.intersects = undefined;
      state.since = win.performance.now();
    }
  }

  function invalidate(root, moved) {
    if (root.nodeType !== 1) return;
    captchaAnchors.forEach((anchor, target) => {
      if (root.contains(target) || target.contains(root)) {
        captchaAnchors.delete(target);
        invalidateAnchor(anchor);
      }
    });
    if (moved) observed.forEach(anchor => {
      if (root.contains(anchor)) invalidateAnchor(anchor);
    });
  }

  function remember(element) {
    let changed = false;
    if (matches(element, CAPTCHA_SELECTOR) && captchas.indexOf(element) === -1) {
      captchas.push(element);
      changed = true;
    }
    if (!matches(element, SCRIPT_SELECTOR) || pending.some(record => record.script === element)) {
      return changed;
    }
    const kind = element.getAttribute('data-sd-widget');
    if ((kind === 'recaptcha' || kind === 'map') && element.getAttribute('data-sd-src')) {
      pending.push({ script: element, kind: kind });
      changed = true;
    }
    return changed;
  }

  function discover(root) {
    if (root.nodeType !== 1 && root.nodeType !== 9 && root.nodeType !== 11) {
      return false;
    }
    let changed = false;
    if (root.nodeType === 1) {
      changed = remember(root);
    }
    if (!root.firstElementChild) return changed;
    const found = root.querySelectorAll(SCRIPT_SELECTOR + ', ' + CAPTCHA_SELECTOR);
    for (let i = 0; i < found.length; i++) {
      if (remember(found[i])) changed = true;
    }
    return changed;
  }

  function activate(record) {
    const inert = record.script;
    const script = doc.createElement('script');
    // Dynamic scripts default to async=true, unlike an ordinary script without
    // an async attribute. Set it explicitly before assigning the URL.
    script.async = inert.hasAttribute('async');
    for (let i = 0; i < inert.attributes.length; i++) {
      const attribute = inert.attributes[i];
      if (attribute.name !== 'type' && attribute.name !== 'src' &&
          attribute.name !== 'data-sd-widget' && attribute.name !== 'data-sd-src') {
        script.setAttribute(attribute.name, attribute.name === 'nonce' ? inert.nonce || attribute.value : attribute.value);
      }
    }
    script.textContent = inert.textContent;
    script.src = inert.getAttribute('data-sd-src');
    record.started = true;
    if (record.kind === 'recaptcha') {
      recaptchaStarted = true;
    }
    // Keep native load/error events and original handlers. A failed script is
    // not retried automatically and cannot create an endless retry loop.
    inert.parentNode.replaceChild(script, inert);
  }

  function refresh(entries) {
    if (stopped) {
      return;
    }
    if (entries) entries.forEach(entry => {
      const state = intersections.get(entry.target);
      if (state && observed.indexOf(entry.target) !== -1 && doc.documentElement.contains(entry.target) &&
          (typeof entry.time !== 'number' || entry.time >= state.since)) {
        state.intersects = entry.isIntersecting;
      }
    });
    pending = pending.filter(record => !record.started && doc.documentElement.contains(record.script));
    captchas = captchas.filter(target => doc.documentElement.contains(target) && matches(target, CAPTCHA_SELECTOR));
    const wanted = [];
    // Measurements belong to this pass only. A popup can change before the
    // next mutation/intersection callback, while forms share many ancestors.
    const visibility = new Map();
    const rectangles = new Map();
    const anchorsByTarget = new Map();
    const rectangle = element => {
      if (!rectangles.has(element)) rectangles.set(element, element.getBoundingClientRect());
      return rectangles.get(element);
    };
    const near = anchor => {
      const state = intersections.get(anchor);
      if (state && typeof state.intersects === 'boolean') return state.intersects;
      const intersects = nearViewport(anchor, doc, win, rectangle);
      if (intersectionObserver) {
        intersections.set(anchor, { intersects, since: win.performance.now() });
      }
      return intersects;
    };
    pending.forEach(record => {
      // The API is shared by every CAPTCHA form. Loading it twice can invoke
      // grecaptchaCallback twice, including for a later lazyRender response.
      if (record.kind === 'recaptcha' && recaptchaStarted) {
        record.started = true;
        record.script.parentNode.removeChild(record.script);
        return;
      }
      const targets = record.kind === 'recaptcha' ? captchas : [mapHost(record.script)];
      const anchors = [];
      let ready = false;
      targets.forEach(target => {
        if (!target) {
          return;
        }
        // MutationObserver will recheck a revealed form. Hidden forms do not
        // need anchor geometry and must not force a layout during app startup.
        // Without that observer keep observing the original layout anchor.
        if (mutationObserver && !isDisplayed(target, doc, win, visibility)) {
          const anchor = record.kind === 'recaptcha' ? captchaAnchors.get(target) : target;
          if (observed.indexOf(anchor) !== -1) anchors.push(anchor);
          return;
        }
        if (!anchorsByTarget.has(target)) {
          let anchor = target;
          if (record.kind === 'recaptcha') {
            anchor = captchaAnchors.get(target);
            if (!anchor || !anchor.contains(target)) anchor = captchaAnchor(target, doc, rectangle);
            if (intersectionObserver) captchaAnchors.set(target, anchor);
          }
          anchorsByTarget.set(target, anchor);
        }
        const anchor = anchorsByTarget.get(target);
        anchors.push(anchor);
        if (isDisplayed(target, doc, win, visibility) && near(anchor)) {
          ready = true;
        }
      });
      if (ready) {
        activate(record);
      } else {
        anchors.forEach(anchor => {
          if (wanted.indexOf(anchor) === -1) {
            wanted.push(anchor);
          }
        });
      }
    });

    // Do not retain observers for loaded widgets. Keep scanning only new
    // subtrees so a later lazyRender response can supply another placeholder.
    const needed = pending.filter(record => !record.started);
    if (intersectionObserver) {
      observed.forEach(anchor => {
        if (wanted.indexOf(anchor) === -1) {
          intersectionObserver.unobserve(anchor);
          intersections.delete(anchor);
        }
      });
      wanted.forEach(anchor => {
        if (observed.indexOf(anchor) === -1) {
          const state = intersections.get(anchor) || {};
          state.since = win.performance.now();
          intersections.set(anchor, state);
          intersectionObserver.observe(anchor);
        }
      });
    }
    observed = wanted;
    intersections.forEach((state, anchor) => {
      if (wanted.indexOf(anchor) === -1) intersections.delete(anchor);
    });
    captchaAnchors.forEach((anchor, target) => {
      if (captchas.indexOf(target) === -1 || wanted.indexOf(anchor) === -1) captchaAnchors.delete(target);
    });
    pending = needed;
  }

  function checkViewport() {
    if (intersectionObserver) {
      observed.forEach(invalidateAnchor);
      captchaAnchors.clear();
    }
    refresh();
  }

  function dispose() {
    stopped = true;
    if (intersectionObserver) {
      intersectionObserver.disconnect();
    }
    if (mutationObserver) {
      mutationObserver.disconnect();
    }
    win.removeEventListener('scroll', checkViewport, true);
    win.removeEventListener('resize', checkViewport);
    pending = [];
    captchas = [];
    observed = [];
    intersections.clear();
    captchaAnchors.clear();
    if (doc[INSTALLATION] === dispose) {
      delete doc[INSTALLATION];
    }
  }

  doc[INSTALLATION] = dispose;
  if (win.IntersectionObserver) {
    intersectionObserver = new win.IntersectionObserver(refresh, { rootMargin: MARGIN + 'px' });
  } else {
    win.addEventListener('scroll', checkViewport, true);
  }
  win.addEventListener('resize', checkViewport);
  if (win.MutationObserver) {
    mutationObserver = new win.MutationObserver(records => {
      let changed = false;
      const roots = new Set();
      const attributes = new Set();
      records.forEach(record => {
        if (record.type === 'attributes') {
          attributes.add(record.target);
        } else {
          for (let i = 0; i < record.addedNodes.length; i++) {
            roots.add(record.addedNodes[i]);
            invalidate(record.addedNodes[i], true);
          }
          for (let i = 0; i < record.removedNodes.length; i++) invalidate(record.removedNodes[i], true);
          // Structural changes can move an existing widget into the viewport.
          if (pending.length) changed = true;
        }
      });
      attributes.forEach(target => {
        invalidate(target, false);
        if (remember(target)) changed = true;
        // A sibling class/id can change visibility through a CSS selector
        // without moving the observed box or producing an intersection event.
        if (pending.length) changed = true;
      });
      roots.forEach(root => {
        for (let parent = root.parentNode; parent; parent = parent.parentNode) {
          if (roots.has(parent)) return;
        }
        if (discover(root)) changed = true;
      });
      if (changed) refresh();
    });
    mutationObserver.observe(doc.documentElement, {
      subtree: true, childList: true, attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'id']
    });
  }
  discover(doc);
  refresh();
  return dispose;
}
